import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useCreateCategory, useUpdateCategory } from '../../../api/mutations/menu.mutations';
import type { MenuCategory } from '../../../api/queries/menu.queries';

interface CategoryEditModalProps {
  category?: MenuCategory;
  onClose: () => void;
  onSuccess: () => void;
}

function CategoryEditModal({ category, onClose, onSuccess }: CategoryEditModalProps) {
  const { t } = useTranslation();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory(category?._id || '');

  const [nameEn, setNameEn] = useState(category?.name.en || '');
  const [nameTh, setNameTh] = useState(category?.name.th || '');
  const [displayOrder, setDisplayOrder] = useState(category?.displayOrder?.toString() || '0');
  const [isActive, setIsActive] = useState(category?.isActive ?? true);
  const [errors, setErrors] = useState<{ nameEn?: string; nameTh?: string }>({});

  useEffect(() => {
    if (category) {
      setNameEn(category.name.en);
      setNameTh(category.name.th);
      setDisplayOrder(category.displayOrder?.toString() || '0');
      setIsActive(category.isActive);
    }
  }, [category]);

  const validate = () => {
    const newErrors: { nameEn?: string; nameTh?: string } = {};

    if (!nameEn.trim()) {
      newErrors.nameEn = 'English name is required';
    }
    if (!nameTh.trim()) {
      newErrors.nameTh = 'Thai name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payload = {
        name: { en: nameEn, th: nameTh },
        displayOrder: parseInt(displayOrder) || 0,
        isActive,
      };

      if (category) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">
            {category ? t('common.edit') : t('menu.addCategory')}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* English Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Name (EN) <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                errors.nameEn ? 'border-destructive' : ''
              }`}
              placeholder="Enter English name..."
            />
            {errors.nameEn && (
              <p className="text-xs text-destructive mt-1">{errors.nameEn}</p>
            )}
          </div>

          {/* Thai Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Name (TH) <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                errors.nameTh ? 'border-destructive' : ''
              }`}
              placeholder="ใส่ชื่อภาษาไทย..."
            />
            {errors.nameTh && (
              <p className="text-xs text-destructive mt-1">{errors.nameTh}</p>
            )}
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('menu.displayOrder')}
            </label>
            <input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              min="0"
              step="1"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm font-medium">
              {t('menu.active')}
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border bg-background px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CategoryEditModal;
